import { ObservableMap } from 'mobx';
import updateFolderCounts from 'owa-mail-actions/lib/updateFolderCounts';
import { ConversationItem, listViewStore } from 'owa-mail-list-store';
import TableView from 'owa-mail-list-store/lib/store/schema/TableView';
import TableViewConversationRelation from 'owa-mail-list-store/lib/store/schema/TableViewConversationRelation';
import getTableConversationRelation from 'owa-mail-list-store/lib/utils/getTableConversationRelation';
import { action, select } from 'satcheljs/lib/legacy';

export interface MarkConversationReadInListViewStoreState {
    conversationItems: ObservableMap<string, ConversationItem>;
}

export default action('MarkConversationReadInListViewStore')(
    select({
        conversationItems: () => listViewStore.conversationItems,
    })(function markConversationReadInListViewStore(
        rowKeys: string[],
        shouldMarkAsRead: boolean,
        tableView: TableView,
        shouldUpdateFolderCounts: boolean,
        state?: MarkConversationReadInListViewStoreState
    ): void {
        let totalUnreadCountChange = 0;
        const rowIdsVisited: { [id: string]: boolean } = {};

        // Update unread count in each table view conversation relations in the current table view
        rowKeys.forEach(rowKey => {
            if (tableView.virutalSelectAllExclusionList.indexOf(rowKey) >= 0) {
                return;
            }

            // VSO 22066: markConversationsReadInListViewStore needs to update the unread count for all tables for that folder
            const tableConversationRelation = getTableConversationRelation(rowKey, tableView.id);
            const unreadCountChange = calculateUnreadCountChange(
                tableConversationRelation,
                shouldMarkAsRead
            );

            // Update the unread count and global unread count
            tableConversationRelation.unreadCount += unreadCountChange;

            const rowId = tableConversationRelation.id;
            const conversationItem = state.conversationItems.get(rowId);
            conversationItem.globalUnreadCount += unreadCountChange;

            // Update the total unread count change to be used by the folder stitch.
            // Skip the rows that have duplicate rowIds (conversationId) else as
            // these rows will have same unread count
            if (!rowIdsVisited[rowId]) {
                rowIdsVisited[rowId] = true;
                totalUnreadCountChange += unreadCountChange;
            }
        });

        const folderId = tableView.serverFolderId;
        if (shouldUpdateFolderCounts) {
            // Get the total unread count change in the current table view
            updateFolderCounts(
                totalUnreadCountChange,
                0 /* totalCount */,
                folderId,
                true /* isDeltaChange */
            );
        }
    })
);

/**
 * Calculate the delta value of the unread count after mark as read/unread action
 * @param tableConversationRelation the table conversation relation
 * @param shouldMarkAsRead whether the action is mark as read action
 */
function calculateUnreadCountChange(
    tableConversationRelation: TableViewConversationRelation,
    shouldMarkAsRead: boolean
): number {
    const currentUnreadCount = tableConversationRelation.unreadCount;
    return shouldMarkAsRead
        ? -1 * currentUnreadCount
        : tableConversationRelation.itemIds.length - currentUnreadCount;
}
